import {useMutation, useQuery} from '@tanstack/react-query'
import React, {useEffect, useState} from 'react'
import {
    Trans,
    useTranslation,
} from 'react-i18next'

import {
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table'
import {
    MRT_Localization_ID,
} from 'material-react-table/locales/id'
import {
    MRT_Localization_EN,
} from 'material-react-table/locales/en'
import {LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'

import axios from 'axios';
import qs from 'qs';
import Swal from 'sweetalert2'

import DateCell from '../components/DateCell'

import {
    ROUTE_PATHS,
    twoDecimalFormat,
} from '../library/helper'
import SettingRevenueAndProductionForm from "./SettingRevenueAndProductionForm";
import FormModal from "../components/FormModal";
import RevenueCreate from "./RevenueCreate";
import RevenueUpdate from "./RevenueUpdate";
import Tour from "../components/OnboardingTour/Tour";
import {Button} from "@mui/material";
import {useSelector} from "react-redux";

const menuName = 'revenue&production'

const steps = [
    {
        target: 'body',
        content: <Trans i18nKey="revenueProd.tour.content1" components={{b: <b/>}}/>,
        placement: 'center',
    },
    {
        target: '#unit-box',
        content: <Trans i18nKey="revenueProd.tour.content2" components={{b: <b/>}}/>,
        placement: 'right',
        styles: {spotlight: {boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)'}},
    },
    {
        target: '#create-unit-btn',
        content: <Trans i18nKey="revenueProd.tour.content3" components={{b: <b/>}}/>,
        placement: 'right',
        styles: {spotlight: {boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)'}},
    },
    {
        target: '#create-unit-box',
        content: <Trans i18nKey="revenueProd.tour.content4" components={{b: <b/>}}/>,
        placement: 'right',
        styles: {spotlight: {boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)'}},
    },
    {
        target: '#rp-box',
        content: <Trans i18nKey="revenueProd.tour.content5" components={{b: <b/>}}/>,
        placement: 'right',
        styles: {spotlight: {boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)'}},
    },
    {
        target: '#create-rp-btn',
        content: <Trans i18nKey="revenueProd.tour.content6" components={{b: <b/>}}/>,
        placement: 'right',
        styles: {spotlight: {boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)'}},
    },
    {
        target: 'body',
        content: <Trans i18nKey="dashboard.tour.content9"/>,
        placement: 'center',
    },
];

const useClientProductDeletion = () => {
    return useMutation({
        mutationKey: [
            'client-product-deletion',
        ],
        retry: false,
        mutationFn: async (data) => {
            return axios({
                url: [
                    process.env.REACT_APP_BASE_SERVER,
                    '/client-product-deletion',
                ].join(''),
                method: 'POST',
                withCredentials: true,
                data,
            })
        },
    })
}

const useTableUnitLookup = () => {
    return useQuery({
        queryKey: [
            'client-product-summary-lookup',
        ],
        retry: false,
        queryFn: ({signal}) => axios({
            url: [
                process.env.REACT_APP_BASE_SERVER,
                '/client-product-summary-lookup',
            ].join(''),
            method: 'GET',
            withCredentials: true,
            signal,
        }).then(response => {
            return response?.data?.data ?? {}
        }),
    })
}

const useTableUnit = (location, {onEdit} = {}) => {
    const {t, i18n} = useTranslation()

    const {
        data = {},
        isLoading,
        isRefetching,
        refetch,
    } = useTableUnitLookup(location)

    const {
        mutate,
        isPending,
    } = useClientProductDeletion()

    const onDelete = async (id) => {
        Swal.fire({
            title: t('dialog.delete.areYouSure'),
            text: t('dialog.delete.youWontBeAbleToRevertThis'),
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: t('dialog.delete.cancel'),
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('dialog.delete.yesDeleteIt'),
        }).then(async (result) => {
            if (result.isConfirmed) {
                mutate({
                    id,
                }, {
                    onSuccess: () => {
                        Swal.fire({
                            icon: 'success',
                            title: `<h5 style="font-size:1.125rem">${t('dialog.delete.deleted')}</h5>`,
                            html: `<p style="font-size:0.75rem">${t('dialog.delete.yourDataHasBeenDeleted')}</p>`,
                            confirmButtonColor: '#3085d6',
                            confirmButtonText: 'Ok'
                        }).then((result) => {
                            if (result.isConfirmed) {
                                refetch()
                            }
                        })
                    },
                    onError: (error) => {
                        let message = t('dialog.delete.cannotDeleteDataPleaseTryAgainLater')
                        if (error.response?.data?.messageType === 'productLinkedToRevenue') {
                            message = t('dialog.delete.product.productLinkedToRevenue')
                        } else if (typeof error.response?.data?.message === 'string') {
                            message = error.response.data.message
                        } else if (error.message) {
                            message = error.message
                        }
                        Swal.fire({
                            title: `<h5 style="font-size:1.125rem">${t('dialog.delete.cannotDeleteData')}</h5>`,
                            html: `<p style="font-size:0.75rem">${message}</p>`,
                            icon: 'error',
                            showCancelButton: false,
                            confirmButtonColor: '#3085d6',
                            confirmButtonText: 'Ok'
                        })
                    }
                })
            }
        })
    }

    const table = useMaterialReactTable({
        enableBottomToolbar: true,
        enableColumnOrdering: true,
        enableDensityToggle: false,
        enableExpandAll: false,
        enableFullScreenToggle: false,
        enableGlobalFilter: true,
        enablePagination: true,
        enableTableFooter: false,
        enableTopToolbar: true,
        initialState: {density: 'compact'},
        localization: i18n.language === 'id' ? (
            MRT_Localization_ID
        ) : MRT_Localization_EN,
        muiTablePaperProps: {
            elevation: 0,
        },
        columns: [
            {
                accessorKey: 'type',
                header: t('setting.revenueAndProduction.type'),
                size: 100,
                Cell: ({ cell }) => {
                    const v = cell.getValue() || '';
                    return v.charAt(0).toUpperCase() + v.slice(1);
                },
            },
            {
                accessorKey: 'name',
                header: t('setting.revenueAndProduction.label'),
                size: 100,
                Cell: ({row, cell}) => {
                    if (row.original?.is_default) {
                        const badgeClass =
                            row.original.type === 'revenue'
                                ? 'text-bg-primary'
                                : 'text-bg-secondary'

                        return (
                            <>
                                {cell.getValue()}{' '}
                                <span className={`badge ${badgeClass}`}>
          {t('setting.revenueAndProduction.form.default')}
        </span>
                            </>
                        )
                    } else {
                        return cell.getValue()
                    }
                },
            },
            {
                accessorKey: 'unit',
                header: t('setting.revenueAndProduction.unit'),
                size: 100,
                Cell: ({cell}) => cell.getValue(),
            },
            {
                accessorKey: 'id',
                header: t('emission.table.label6'),
                size: 100,
                enableSorting: false,
                enableColumnFilter: false,
                enableGrouping: false,
                Cell: ({cell}) => <div className="d-flex flex-wrap gap-2">
                    <button
                        type="button"
                        className="btn btn-info btn-icon waves-effect waves-light"
                        onClick={() => onEdit?.(cell.getValue())}
                        title={t('general.btnUpdate')}
                    >
                        <i className="ri-pencil-fill"/>
                    </button>
                    <button
                        onClick={() => onDelete(cell.getValue())}
                        type="button"
                        className="btn btn-warning btn-icon waves-effect waves-light"
                    >
                        <i className="ri-delete-bin-2-fill"></i>
                    </button>
                </div>,
            },
        ],
        data: Array.isArray(data.products) ? data.products : [],
        state: {
            isLoading: isLoading || isRefetching || isPending,
        }
    })
    return {table, refetch, dataLength: data.products?.length ?? 0}
}

const useTableLookup = () => {
    return useMutation({
        mutationKey: ['emission/revenue_get'],
        retry: false,
        mutationFn: async (data) => axios({
            url: [
                process.env.REACT_APP_BASE_SERVER,
                '/emission/revenue_get',
            ].join(''),
            method: 'POST',
            withCredentials: true,
            data,
        }).then(response => {
            return response?.data?.data ?? []
        }),
    })
}

const useTable = (location, {onEdit}) => {
    const {t, i18n} = useTranslation()

    const {
        data = [],
        isPending: isLoading,
        mutate: requestRevenues,
    } = useTableLookup()

    useEffect(() => {
        requestRevenues()
    }, [])

    const onDeleteCall = async (data) => {
        const options = {
            withCredentials: true,
            method: 'POST',
            headers: {'content-type': 'application/x-www-form-urlencoded'},
            data: qs.stringify(data),
            url: `${process.env.REACT_APP_BASE_SERVER}/emission/revenue/delete`
        };
        const response = await axios(options);
        if (response.data.success) {
            return true;
        } else {
            return false;
        }
    }

    const onDelete = async (id) => {
        let invalidData = true;
        Swal.fire({
            title: 'Are you sure?',
            text: 'You won\'t be able to revert this!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                const data = {
                    'id': id,
                };
                const result = onDeleteCall(data)
                if (result) {
                    invalidData = false;
                }

                if (invalidData) {
                    Swal.fire({
                        icon: 'error',
                        title: '',
                        html: '<p>Cannot delete data, please try again later</p>',
                        showCancelButton: false,
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: 'Ok'
                    })
                } else {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted',
                        text: 'Your data has been deleted.',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: 'Ok'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            window.location.reload();
                        }
                    })
                }
            }
        })
    }

    const table = useMaterialReactTable({
        enableBottomToolbar: true,
        enableColumnOrdering: true,
        enableDensityToggle: false,
        enableExpandAll: false,
        enableFullScreenToggle: false,
        enableGlobalFilter: false,
        enablePagination: true,
        enableTableFooter: false,
        enableTopToolbar: true,
        initialState: {density: 'compact'},
        localization: i18n.language === 'id' ? MRT_Localization_ID : MRT_Localization_EN,
        muiTablePaperProps: {
            elevation: 0,
        },
        columns: [
            {
                size: 100,
                accessorKey: 'revenue_date_time',
                header: t('emission.table.label1'),
                // Transform data before processing so sorting works
                accessorFn: (row) => new Date(row.revenue_date_time),
                Cell: ({row}) => <DateCell dateTime={row.original.revenue_date_time}/>,
                sortingFn: (rowA, rowB) => {
                    const a = rowA.original.revenue_date_time
                    const b = rowB.original.revenue_date_time

                    if (a > b) {
                        return 1
                    } else if (b > a) {
                        return -1
                    } else {
                        return 0
                    }
                },
            },
            {
                size: 100,
                accessorKey: 'revenue_company',
                header: t('emission.table.label2'),
            },
            {
                size: 100,
                accessorKey: 'product_name',
                header: t('setting.revenueAndProduction.label'),
            },
            {
                size: 100,
                accessorKey: 'revenue_amount',
                header: t('emission.table.label5'),
                Cell: ({cell, row}) => <div className='d-flex flex-column flex-wrap gap-2'>
          <span>
            {twoDecimalFormat(cell.getValue(), i18n.language)} {row.original.product_unit}
          </span>
                </div>,
            },
            {
                size: 100,
                accessorKey: 'revenue_uuid',
                header: t('emission.table.label6'), // Action column
                enableSorting: false,
                enableColumnFilter: false,
                enableGrouping: false,
                Cell: ({cell}) => <div className='d-flex flex-wrap gap-2'>
                    <button
                        type="button"
                        className="btn btn-info btn-icon waves-effect waves-light"
                        onClick={() => onEdit?.(cell.getValue())}
                        title={t('general.btnUpdate')}
                    >
                        <i className="ri-pencil-fill"/>
                    </button>
                    <button
                        onClick={() => onDelete(cell.getValue())}
                        type='button'
                        className='btn btn-warning btn-icon waves-effect waves-light'
                    >
                        <i className='ri-delete-bin-2-fill'></i>
                    </button>
                </div>,
            },
        ],
        data,
        state: {
            isLoading: isLoading,
        }
    })

    return {table, requestRevenues}
}

export default function RevenueV2() {
    const [tourRun, setTourRun] = useState(false)
    const account = useSelector((state) => state.account.value)
    const {t} = useTranslation()

    useEffect(() => {
        if (!Array.isArray(account?.tours) || !account.tours.includes(menuName)) {
            setTourRun(true);
        }
    }, [account?.tours]);


    const {table: tableRevenue, requestRevenues: refetchRevenue} = useTable(undefined, {
        onEdit: (id) => {
            setEditingRevenueId(id);
            setRevenueUpdateModalOpen(true)
        }
    })
    const {table: tableUnit, refetch: refetchUnit, dataLength} = useTableUnit(undefined, {
        onEdit: (id) => {
            setEditingUnitId(id);
            setUnitModalOpen(true)
        }
    })
    const [unitModalOpen, setUnitModalOpen] = useState(false)
    const [editingUnitId, setEditingUnitId] = useState(null)
    const openCreateUnit = () => {
        setEditingUnitId(null);
        setUnitModalOpen(true)
    }
    const closeUnitModal = () => {
        setUnitModalOpen(false);
        setEditingUnitId(null)
    }

    const [revenueModalOpen, setRevenueModalOpen] = useState(false)
    const openCreateRevenue = () => {
        setRevenueModalOpen(true)
    }
    const closeRevenueModal = () => {
        setRevenueModalOpen(false)
    }

    const [revenueUpdateModalOpen, setRevenueUpdateModalOpen] = useState(false)
    const [editingRevenueId, setEditingRevenueId] = useState(null)
    const openUpdateRevenue = () => {
        setEditingRevenueId(null);
        setRevenueUpdateModalOpen(true)
    }
    const closeRevenueUpdateModal = () => {
        setRevenueUpdateModalOpen(false);
        setEditingRevenueId(null)
    }

    const handleUnitSuccess = () => {
        refetchUnit?.()
        refetchRevenue?.()
        closeUnitModal()
    }

    const handleRevenueSuccess = () => {
        refetchRevenue?.()
        closeRevenueModal()
    }

    const handleRevenueUpdateSuccess = () => {
        refetchRevenue?.()
        closeRevenueUpdateModal()
    }

    return (
        <>
            <Tour run={tourRun} onEnd={() => setTourRun(false)} steps={steps} menu={menuName}/>
            <div className='page-content'>
                <div className='container-fluid'>
                    <div className='row'>
                        <div className='col-12'>
                            <div className='page-title-box d-sm-flex align-items-center justify-content-between'>
                                <h4 className='mb-sm-0'>
                                    {t('menu.label22')}
                                </h4>
                                <Button variant="contained" color="primary"
                                        onClick={() => setTourRun(true)}
                                        aria-label="Start Revenue & Production Tour"
                                >
                                    {t('dashboard.startTour')}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div id="unit-box" className='row'>
                        <div className='col-lg-12'>
                            <div className='card'>
                                <div className='card-header'>
                                    <h5 className='card-title mb-0'>
                                        {t('menu.label23')}
                                    </h5>
                                    <br/><br/>
                                    <button
                                        id={"create-unit-btn"}
                                        type="button"
                                        className="btn btn-success"
                                        onClick={openCreateUnit}
                                    >
                                        {t('general.btnCreate')}
                                    </button>
                                </div>
                                <div className='card-body'>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MaterialReactTable table={tableUnit}/>
                                    </LocalizationProvider>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


                <div id="rp-box" className='container-fluid'>
                    <div className='row'>
                        <div className='col-lg-12'>
                            <div className='card'>
                                <div className='card-header'>
                                    <h5 className='card-title mb-0'>
                                        {t('menu.label22')}
                                    </h5>
                                    <br/><br/>
                                    <button
                                        id="create-rp-btn"
                                        type="button"
                                        className="btn btn-success"
                                        onClick={openCreateRevenue}
                                        disabled={dataLength === 0}
                                    >
                                        {t('general.btnCreate')}
                                    </button>
                                </div>
                                <div className='card-body'>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MaterialReactTable table={tableRevenue}/>
                                    </LocalizationProvider>
                                </div>
                            </div>
                        </div>
                    </div>
                    <FormModal
                        open={unitModalOpen}
                        onClose={closeUnitModal}
                        title={editingUnitId ? t('general.btnUpdate') : t('general.btnCreate')}
                        onSuccess={handleUnitSuccess}
                        isDialogModal={true}
                        size="lg"
                        centered
                        scrollable
                        bodyClassName="p-0"
                    >
                        <SettingRevenueAndProductionForm key={editingUnitId ?? 'create'} id={editingUnitId}/>
                    </FormModal>
                    <FormModal
                        open={revenueModalOpen}
                        onClose={closeRevenueModal}
                        title={t('general.btnCreate')}
                        onSuccess={handleRevenueSuccess}
                        isDialogModal={true}
                        size="lg"
                        centered
                        scrollable
                        bodyClassName="p-0"
                    >
                        <RevenueCreate key={editingUnitId ?? 'create'} id={editingUnitId}/>
                    </FormModal>
                    <FormModal
                        open={revenueUpdateModalOpen}
                        onClose={closeRevenueUpdateModal}
                        title={t('general.btnUpdate')}
                        onSuccess={handleRevenueUpdateSuccess}
                        isDialogModal={true}
                        size="lg"
                        centered
                        scrollable
                        bodyClassName="p-0"
                    >
                        <RevenueUpdate key={editingRevenueId ?? 'create'} id={editingRevenueId}/>
                    </FormModal>
                </div>
            </div>
        </>
    )
}
